import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronsUpDown, Eye, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { toast } from '@/hooks/use-toast'
import { authApi } from '@/features/auth/api/authApi'
import { masqueradeApi, type AssumableArtist } from '../api/masqueradeApi'
import { cn } from '@/lib/utils'

/**
 * Manager-only control for viewing the platform as a given artist.
 *
 * Rendered in the top bar for a MANAGER, and it is the only affordance — the server refuses
 * anyone else, so this hiding itself is convenience rather than the security boundary.
 *
 * Searching happens on the SERVER. With ~1300 artists, filtering a fully-downloaded list in the
 * browser would mean shipping the whole roster to every manager on every page load, and matching
 * only what happened to arrive.
 */
export function MasqueradePicker() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const masquerade = useAuthStore((s) => s.masquerade)
  const beginInStore = useAuthStore((s) => s.beginMasquerade)
  const endInStore = useAuthStore((s) => s.endMasquerade)

  const { data: artists = [], isFetching } = useQuery({
    queryKey: ['masquerade-artists', query],
    queryFn: () => masqueradeApi.listArtists(query),
    // Only while the picker is open: this is a manager tool, not something to poll behind a
    // closed popover on every page.
    enabled: open && !masquerade,
    staleTime: 30_000,
  })

  const begin = useMutation({
    mutationFn: async (artist: AssumableArtist) => {
      const actorToken = useAuthStore.getState().authToken
      const actorUser = useAuthStore.getState().user
      if (!actorToken || !actorUser) throw new Error('Not signed in')

      const session = await masqueradeApi.begin(artist.id)
      // Load the artist as the API sees them under the new token, rather than assuming the shape
      // from the picker row — the header should show what their session actually resolves to.
      const artistUser = await authApi.getCurrentUserWithToken(session.token)

      beginInStore(
        {
          artistId: artist.id,
          artisticName: session.artist.artisticName,
          actorToken,
          actorUser,
        },
        artistUser,
        session.token,
      )
      return session
    },
    onSuccess: () => {
      setOpen(false)
      setQuery('')
      // Everything cached belongs to the previous identity.
      queryClient.clear()
      navigate('/app')
    },
  })

  if (masquerade) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5">
        <Eye className="h-4 w-4 text-amber-600" />
        <span className="text-sm">
          Viewing as <strong>{masquerade.artisticName}</strong>
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2"
          onClick={() => {
            // Exiting has dumped a manager at the sign-in screen rather than back at their own
            // dashboard. The restore itself is local and cannot fail — it just writes the stored
            // actor credential back — so the failure is that the credential it restores is no
            // longer good, and nothing checked. The manager is then silently signed out, which
            // reads as "the app logged me out" rather than "your session expired while you were
            // viewing as someone else".
            //
            // Check before navigating, and say which it was.
            const m = useAuthStore.getState().masquerade
            if (!m?.actorToken || !m?.actorUser) {
              toast({
                title: 'Your own session is gone',
                description:
                  'Signing in again will return you to your account. Nothing you did as this artist is lost.',
                variant: 'destructive',
              })
            }
            endInStore()
            queryClient.clear()
            navigate('/app')
          }}
        >
          <X className="mr-1 h-3 w-3" />
          Exit
        </Button>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" aria-expanded={open}>
          <Eye className="h-4 w-4" />
          <span className="hidden md:inline">View as artist</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email or id…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {isFetching && artists.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Searching…</div>
            ) : (
              <CommandEmpty>No artist matches that.</CommandEmpty>
            )}
            <CommandGroup>
              {artists.map((artist) => (
                <CommandItem
                  key={artist.id}
                  value={String(artist.id)}
                  disabled={begin.isPending}
                  onSelect={() => begin.mutate(artist)}
                >
                  <Check className={cn('mr-2 h-4 w-4 opacity-0')} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">{artist.artisticName}</span>
                    <span className="truncate text-xs text-muted-foreground">{artist.email}</span>
                  </div>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                    {artist.artworkCount}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {begin.isError && (
          <p className="border-t px-3 py-2 text-xs text-destructive">
            {(begin.error as Error).message}
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}
